'use client';

import { useKeenSlider } from 'keen-slider/react';
import React, { useState } from 'react';
import classNames from 'classnames';
import { GetPageComponentViewModel } from '@/types/cml-api';
import { NavigationCarousel } from '@/features/carousel';
import { autoSwitch } from '@/features/carousel/plugins';
import { CarouselArrowType, CarouselOptions, DotsType } from '@/features/carousel/types';
import Arrow from './arrow';
import CarouselMediumItem from './carousel-medium-item';
import Dots from './dots';
import Skeleton from './skeleton';
import { deviceTypeClassGenerator } from '../../_utils/device-type-handlers';
import { IsLoadingLazyProps } from '../../type';
interface Props extends IsLoadingLazyProps {
  data: GetPageComponentViewModel;
  index: number;
  isLandingPage?: boolean;
}
export function CarouselMedium({
  data,
  isLazy,
  index,
  isLandingPage
}: Props) {
  const hasSingleItem = data.items?.length === 1;
  const origin = 'center';
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    rtl: data.items?.length && data.items.length > 2 ? true : false,
    loop: !hasSingleItem,
    slides: {
      perView: 1.3,
      spacing: 24,
      origin
    },
    breakpoints: {
      '(max-width: 767px)': {
        slides: {
          perView: 1.1,
          spacing: 10,
          origin
        }
      }
    },
    defaultAnimation: hasSingleItem ? undefined : {
      duration: 2500
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    }
  }, [autoSwitch]);
  const carouselItems = data.items?.map((item, idx) => <CarouselMediumItem key={item?.id || item.link} index={idx} isLazy={isLazy || idx !== 0} sortKey={data?.sortKey} componentId={data?.id} {...item} />);
  if (index === 0 || !isLandingPage && index === 1) {
    return <div className={classNames('homepage-container group relative min-h-[200px] lg:min-h-[330px]', deviceTypeClassGenerator(data.deviceTypes || ''))}>
                <div ref={sliderRef} id={data.id || ''} className={classNames('keen-slider', {
        invisible: !loaded
      })}>
                    {carouselItems}
                </div>

                {!hasSingleItem && loaded && instanceRef.current && <>
                        <div className='absolute bottom-1/2 right-[5.5%] z-10 hidden translate-y-1/2 items-center gap-3 lg:flex'>
                            <Arrow direction='right' onClick={e => {
            e.stopPropagation();
            instanceRef.current?.prev();
          }} />
                        </div>

                        <div className='absolute bottom-1/2 left-[5.5%] z-10 hidden translate-y-1/2 items-center gap-3 lg:flex'>
                            <Arrow direction='left' onClick={e => {
            e.stopPropagation();
            instanceRef.current?.next();
          }} />
                        </div>

                        <Dots instanceRef={instanceRef} currentSlide={currentSlide} />
                    </>}

                {!loaded && <Skeleton />}
            </div>;
  }
  const options: CarouselOptions = {
    mode: 'snap',
    perView: 1.3,
    loop: !hasSingleItem,
    rtl: data.items?.length && data.items?.length > 2 ? true : false,
    breakPoints: {
      desktop: {
        perView: 1.3,
        spacing: 24,
        origin
      },
      mobile: {
        perView: 1.1,
        spacing: 10,
        origin
      }
    }
  };
  return <div className={classNames('homepage-container relative min-h-[200px] lg:min-h-[330px]', deviceTypeClassGenerator(data.deviceTypes || ''))} data-sentry-component="CarouselMedium" data-sentry-source-file="carousel-medium.tsx">
            <NavigationCarousel arrowType={CarouselArrowType.MediumCarousel} options={options} skeleton={<Skeleton />} dotsType={DotsType.HomePage} data-sentry-element="NavigationCarousel" data-sentry-source-file="carousel-medium.tsx">
                {carouselItems}
            </NavigationCarousel>
        </div>;
}